import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Badge,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { FaClock, FaSave, FaEdit } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getWorkingHours, updateWorkingHours } from '../services/consultantService';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import CustomModal from '../components/Modal';
import WorkingHoursForm from '../components/forms/WorkingHoursForm';

function WorkingHours() {
  const formRef = useRef(null);
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // اگر consultant در URL وجود دارد، از آن استفاده کن، در غیر این صورت از user فعلی
  const consultantId = searchParams.get('consultant') || user?.id;
  
  // React Query hooks
  const queryClient = useQueryClient();
  
  // دریافت ساعات کاری مشاور
  const {
    data: workingHoursData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['workingHours', consultantId],
    () => getWorkingHours(consultantId),
    {
      enabled: !!consultantId,
      staleTime: 5 * 60 * 1000, // 5 دقیقه
      retry: 1
    }
  );

  // Mutation برای بروزرسانی ساعات کاری
  const updateWorkingHoursMutation = useMutation(
    (data) => updateWorkingHours(consultantId, data),
    {
      onSuccess: () => {
        toast.success('ساعات کاری با موفقیت بروزرسانی شد');
        queryClient.invalidateQueries(['workingHours']);
        setIsModalOpen(false);
      },
      onError: (error) => {
        toast.error(error.message || 'خطا در بروزرسانی ساعات کاری');
      }
    }
  );

  // تابع باز کردن modal برای ویرایش
  const handleEditWorkingHours = () => {
    setIsModalOpen(true);
  };

  // تابع بستن modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // تابع ارسال فرم
  const handleSubmitForm = (data) => {
    updateWorkingHoursMutation.mutate(data);
  };

  // تابع تبدیل نام روز به فارسی
  const getDayName = (day) => {
    const dayNames = {
      saturday: 'شنبه',
      sunday: 'یکشنبه',
      monday: 'دوشنبه',
      tuesday: 'سه‌شنبه',
      wednesday: 'چهارشنبه',
      thursday: 'پنج‌شنبه',
      friday: 'جمعه'
    };
    return dayNames[day] || day;
  };

  // تابع نمایش وضعیت روز
  const getDayStatus = (day) => {
    if (!day.isWorking) {
      return <Badge colorScheme="red">تعطیل</Badge>;
    }
    return <Badge colorScheme="green">فعال</Badge>;
  };

  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری ساعات کاری...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  const workingHours = workingHoursData?.data?.workingHours || [
    { day: 'saturday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' },
    { day: 'sunday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' },
    { day: 'monday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' },
    { day: 'tuesday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' },
    { day: 'wednesday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' },
    { day: 'thursday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' },
    { day: 'friday', isWorking: false, startTime: '09:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00' }
  ];
  const sessionDuration = workingHoursData?.data?.sessionDuration || 60;

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
      <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
        <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
          {/* Header */}
          <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            ساعات کاری
            {searchParams.get('consultant') && workingHoursData?.data?.consultantName && (
              <Text as="span" fontSize="lg" color="gray.500" fontWeight="normal">
                {' '}- {workingHoursData.data.consultantName}
              </Text>
            )}
          </Text>
          <Text fontSize="sm" color="gray.500">
            مدیریت ساعات کاری و طول جلسات مشاوره
          </Text>
        </Box>
        <Button
          leftIcon={<FaEdit />}
          colorScheme="blue"
          onClick={handleEditWorkingHours}
        >
          ویرایش ساعات کاری
        </Button>
      </Flex>

      {/* طول جلسه */}
      <Box mb={6} bg={bgCard} border="1px" borderColor={borderColor} borderRadius="lg" p={6}>
        <HStack mb={4}>
          <FaClock />
          <Heading size="md">طول هر جلسه</Heading>
        </HStack>
        <Text fontSize="lg" fontWeight="medium">
          {sessionDuration} دقیقه
        </Text>
        <Text fontSize="sm" color="gray.500" mt={1}>
          مدت زمان هر جلسه مشاوره
        </Text>
      </Box>

      {/* ساعات کاری روزهای هفته */}
      <Box bg={bgCard} border="1px" borderColor={borderColor} borderRadius="lg" p={6}>
        <HStack mb={4}>
          <FaClock />
          <Heading size="md">ساعات کاری روزهای هفته</Heading>
        </HStack>
        <VStack spacing={4} align="stretch">
          {workingHours.map((day, index) => (
            <Box key={day.day}>
              <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="md">
                <HStack spacing={4}>
                  <Text fontWeight="medium" minW="80px">
                    {getDayName(day.day)}
                  </Text>
                  {getDayStatus(day)}
                </HStack>
                
                {day.isWorking && (
                  <HStack spacing={6} wrap="wrap">
                    <Box>
                      <Text fontSize="sm" color="gray.500">شروع</Text>
                      <Text fontWeight="medium">{day.startTime}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.500">پایان</Text>
                      <Text fontWeight="medium">{day.endTime}</Text>
                    </Box>
                    {(day.breakStart && day.breakEnd) && (
                      <Box>
                        <Text fontSize="sm" color="orange.500" fontWeight="medium">استراحت اصلی</Text>
                        <Badge colorScheme="orange" variant="outline">
                          {day.breakStart} - {day.breakEnd}
                        </Badge>
                      </Box>
                    )}
                    {Array.isArray(day.breaks) && day.breaks.length > 0 ? (
                      <Box>
                        <Text fontSize="sm" color="blue.500" fontWeight="medium">
                          استراحت‌های دائمی ({day.breaks.length})
                        </Text>
                        <VStack spacing={1} align="flex-start" mt={1}>
                          {day.breaks.map((br, idx) => (
                            <Badge key={idx} colorScheme="blue" variant="outline" fontSize="xs">
                              {br.startTime} - {br.endTime}
                            </Badge>
                          ))}
                        </VStack>
                      </Box>
                    ) : (
                      !(day.breakStart && day.breakEnd) && (
                        <Box>
                          <Text fontSize="sm" color="gray.400" fontStyle="italic">
                            بدون استراحت
                          </Text>
                        </Box>
                      )
                    )}
                  </HStack>
                )}
              </Flex>
              {index < workingHours.length - 1 && <Divider mt={4} />}
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Modal برای ویرایش */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="ویرایش ساعات کاری"
        size="xl"
        submitButtonText="ذخیره تغییرات"
        onSubmit={() => formRef.current?.requestSubmit()}
        isLoading={updateWorkingHoursMutation.isLoading}
      >
        <WorkingHoursForm
          ref={formRef}
          workingHours={workingHours}
          sessionDuration={sessionDuration}
          onSubmit={handleSubmitForm}
          isLoading={updateWorkingHoursMutation.isLoading}
        />
      </CustomModal>
        </Box>
      </Box>
    </Flex>
  );
}

export default WorkingHours;
